import {
  Button,
  Center,
  Flex,
  FormControl,
  FormHelperText,
  FormLabel,
  HStack,
  Input,
  Select,
  Spinner,
  Stack,
  Text,
  Textarea,
  useToast,
} from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Currency,
  SourcingEventV2Dto,
  useAwardSourcingEventMutation,
  useGetEvaluationQuery,
} from "../../../../autogen/bff-api";
import { useApiError } from "../../../../common/errors/useApiError";
import { AwardBidsTable } from "./AwardBidsTable";
import { pickDefaultWinner } from "./pickDefaultWinner";

export const SelectWinner = ({ event }: { event: SourcingEventV2Dto }) => {
  const { t } = useTranslation();
  const toast = useToast();
  const apiError = useApiError();
  const { data: evaluation, isLoading, isError } = useGetEvaluationQuery({ eventId: event.id });
  const [award, { isLoading: isAwarding }] = useAwardSourcingEventMutation();

  const [winnerId, setWinnerId] = useState("");
  const [internalNote, setInternalNote] = useState("");
  const [currency, setCurrency] = useState<Currency>(event.publicContext.estimatedValue?.currency ?? Currency.Nok);
  const [amount, setAmount] = useState(
    event.publicContext.estimatedValue?.value != null ? String(event.publicContext.estimatedValue.value) : "",
  );

  // Default to the top-ranked bid, and re-default if the current pick disappears from a refetch
  // (e.g. a bid is disqualified mid-flow) so the Award button never submits a stale winningBidId.
  useEffect(() => {
    if (!evaluation) return;
    const stillValid = winnerId !== "" && evaluation.bids.some((b) => b.bidId === winnerId);
    if (!stillValid) setWinnerId(pickDefaultWinner(evaluation.bids) ?? "");
  }, [evaluation, winnerId]);

  if (isLoading) {
    return (
      <Center py="16">
        <Spinner color="smPrimary" />
      </Center>
    );
  }
  if (isError || !evaluation) {
    return (
      <Center py="16">
        <Text color="smError">{t("Could not load the evaluation.")}</Text>
      </Center>
    );
  }
  if (evaluation.bids.length === 0) {
    return (
      <Center py="16">
        <Text color="smMuted">{t("No delivered bids to award yet.")}</Text>
      </Center>
    );
  }

  const submit = async () => {
    if (!winnerId) return;
    const parsed = amount.trim() === "" ? null : Number(amount);
    try {
      await award({
        eventId: event.id,
        awardSourcingEventRequest: {
          winningBidId: winnerId,
          internalNote: internalNote.trim() || null,
          eventValue: parsed != null && !Number.isNaN(parsed) ? { value: parsed, currency } : null,
        },
      }).unwrap();
      toast({ title: t("Event awarded"), status: "success", position: "top-right" });
    } catch (e) {
      apiError.trigger(e);
    }
  };

  return (
    <Stack spacing="6">
      <AwardBidsTable evaluation={evaluation} mode="select" selectedBidId={winnerId} onSelect={setWinnerId} />

      <FormControl>
        <FormLabel>{t("Estimated contract value")}</FormLabel>
        <HStack>
          <Input
            type="number"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            bg="smBackground"
            maxW="xs"
            placeholder="0"
          />
          <Select value={currency} onChange={(e) => setCurrency(e.target.value as Currency)} bg="smBackground" maxW="32">
            {Object.values(Currency).map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </Select>
        </HStack>
      </FormControl>

      <FormControl>
        <FormLabel>{t("Internal note")}</FormLabel>
        <Textarea value={internalNote} onChange={(e) => setInternalNote(e.target.value)} bg="smBackground" rows={2} />
        <FormHelperText>{t("Only visible to your organization.")}</FormHelperText>
      </FormControl>

      <Flex justify="end" align="center" gap="3">
        <Text fontSize="sm" color="smMuted">
          {t("You'll prepare and send award letters to each supplier after awarding.")}
        </Text>
        <Button colorScheme="teal" onClick={submit} isLoading={isAwarding} isDisabled={!winnerId}>
          {t("Award to selected supplier")}
        </Button>
      </Flex>
    </Stack>
  );
};
